import { THREE, RoundedBoxGeometry, OrbitControls, RectAreaLightUniformsLib } from './three-setup.js';

/* 
  Overview:
  1) The script finds all elements with the class .threejs-container.
  2) For each container, it checks if a front URL is provided via the "data-front-url" attribute.
     If not, it falls back to the "data-product-image".
  3) It reads the personal flag from "data-personal" and configures lighting, materials, and background.
  4) For personal models, the front face remains the personalized picture.
     On the back, in addition to the large faces colored #eaeaea, 
     we add an extra thin metallic back layer (with rounded corners, now color #f2f2f2),
     and on top of that, a small black square.
*/

// Global geometry caching for performance
let mainBoxGeometry = null;
let backSquareGeometry = null; // For the small black square (back element)
let layerGeometry = null;      // Thin back layer geometry

// Single texture loader
const textureLoader = new THREE.TextureLoader();

// Cache for the standard back texture
let cachedStandardBackTexture = null;

document.addEventListener("DOMContentLoaded", () => {
  const jsDisabledMessage = document.getElementById("js-disabled-message");
  if (jsDisabledMessage) {
    jsDisabledMessage.style.display = "none"; // Hide the 'enable javascript' message
  }

  // Initialize RectAreaLight required uniforms
  RectAreaLightUniformsLib.init();

  const containers = document.querySelectorAll(".threejs-container");
  if (!containers || containers.length === 0) {
    console.warn("No .threejs-container elements found on the page.");
    return;
  }

  // Pre-calculate main box geometry only once (2.7 × 3.7 × 0.04 with rounded edges)
  if (!mainBoxGeometry) {
    mainBoxGeometry = new RoundedBoxGeometry(2.7, 3.7, 0.04, 6, 0.2);
  }

  // Pre-calculate the small black square geometry (for the back)
  if (!backSquareGeometry) {
    const smallerDim = 2.75;
    const backSquareSize = 0.255 * smallerDim; // ~0.70125
    const backSquareThickness = 0.05;
    const cornerRadius = 0.05;
    const segments = 6;
    backSquareGeometry = new RoundedBoxGeometry(
      backSquareSize,
      backSquareSize,
      backSquareThickness,
      segments,
      cornerRadius
    );
  }

  // Pre-calculate the thin layer geometry (for the back, slightly smaller than the main box)
  if (!layerGeometry) {
    // Dimensions a bit smaller than the main box to reveal a subtle border
    const layerWidth = 2.6;
    const layerHeight = 3.6;
    const layerDepth = 0.01;
    const layerSegments = 6;
    const layerCornerRadius = 0.08;
    layerGeometry = new RoundedBoxGeometry(
      layerWidth,
      layerHeight,
      layerDepth,
      layerSegments,
      layerCornerRadius
    );
  }

  containers.forEach((container) => {
    const productImageUrl = container.getAttribute("data-product-image") || "";

    // Detect personal mode
    const isPersonal = container.getAttribute("data-personal") || productImageUrl.includes("personal");

    // Set container dimensions if not already set
    container.style.width = container.style.width || "100%";
    container.style.height = container.style.height || "100%";
    const width = container.clientWidth || 320;
    const height = container.clientHeight || 500;

    // Create scene & camera
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xffffff); // white background

    // Camera from above
    const camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
    camera.position.set(0, 0, 4);

    // Renderer
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      powerPreference: "high-performance",
    });
    renderer.setSize(width, height);
    renderer.domElement.style.borderRadius = "20px";
    renderer.setPixelRatio(window.devicePixelRatio || 1);
    renderer.shadowMap.enabled = false;
    container.appendChild(renderer.domElement);

    // OrbitControls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.25;
    controls.maxDistance = 4.5;
    controls.minDistance = 2;
    controls.screenSpacePanning = false;
    controls.autoRotate = false;
    controls.autoRotateSpeed = 2;
    controls.minPolarAngle = Math.PI / 8; // ~22.5° from the top
    controls.maxPolarAngle = Math.PI / 2; // horizontal view

    // Enhanced auto-rotate logic
    let autoRotateMode = "default";
    const oscillationAmplitude = 0.2618; // ~15° in radians
    const oscillationSpeed = 1;
    let userInteracted = false;
    let autoRotateTimeout;
    function startInteraction() {
      userInteracted = true;
      autoRotateMode = "full";
      controls.autoRotate = false;
    }
    function endInteraction() {
      clearTimeout(autoRotateTimeout);
      autoRotateTimeout = setTimeout(() => {
        if (userInteracted) {
          controls.autoRotate = true;
        }
      }, 2000);
    }
    controls.addEventListener("start", startInteraction);
    controls.addEventListener("end", endInteraction);

    // Lights
    let AMBIENT_LIGHT_INTENSITY = 1.2;
    let RECT_LIGHT_INTENSITY = 1.2;
    let POINT_LIGHT_INTENSITY = 0.5;
    let BACK_POINT_LIGHT_INTENSITY = 0.04;
    if (isPersonal) {
      AMBIENT_LIGHT_INTENSITY = 1;
      RECT_LIGHT_INTENSITY = 0.6;
      POINT_LIGHT_INTENSITY = 0.25;
      BACK_POINT_LIGHT_INTENSITY = 0.02;
    }
    const ambientLight = new THREE.AmbientLight(0xffffff, AMBIENT_LIGHT_INTENSITY);
    scene.add(ambientLight);

    const rectLight = new THREE.RectAreaLight(0xffffff, RECT_LIGHT_INTENSITY, 10, 18);
    rectLight.position.set(-3, -3, 10);
    rectLight.lookAt(0, 0, 0);
    scene.add(rectLight);

    const pointLight = new THREE.PointLight(0xffffff, POINT_LIGHT_INTENSITY, 1100);
    scene.add(pointLight);

    const pointLightBack = new THREE.PointLight(0xffffff, BACK_POINT_LIGHT_INTENSITY, 1500, 1);
    pointLightBack.position.set(-2, -5, -10);
    scene.add(pointLightBack);

    // Main product box materials
    const standardBack = "https://cdn.shopify.com/s/files/1/0868/2209/3144/files/3d-model-back-sample.jpg?v=1736957788";
    const textureUrls = [
      standardBack,  // face 1
      standardBack,  // face 2
      standardBack,  // face 3
      standardBack,  // face 4
      productImageUrl, // face 5 (dynamic/front)
      standardBack   // face 6
    ];
    if (!cachedStandardBackTexture) {
      cachedStandardBackTexture = textureLoader.load(standardBack);
      cachedStandardBackTexture.anisotropy = renderer.capabilities.getMaxAnisotropy() || 1;
    }
    function createMaterial(url, metalness, roughness) {
      if (!url) {
        return new THREE.MeshStandardMaterial({ color: 0x888888, metalness, roughness });
      }
      if (url === standardBack && cachedStandardBackTexture) {
        return new THREE.MeshStandardMaterial({
          map: cachedStandardBackTexture,
          metalness,
          roughness
        });
      }
      const texture = textureLoader.load(url);
      texture.anisotropy = renderer.capabilities.getMaxAnisotropy() || 1;
      return new THREE.MeshStandardMaterial({
        map: texture,
        metalness,
        roughness
      });
    }
    const materials = textureUrls.map((url, idx) => {
      if (isPersonal) {
        if (idx === 4) {
          // Front face uses the dynamic image.
          return createMaterial(url, 0.2, 0.1);
        }
        // All other faces use a solid color (#eaeaea)
        return new THREE.MeshStandardMaterial({
          color: 0xeaeaea,
          metalness: 0.2,
          roughness: 1
        });
      } else {
        return createMaterial(
          url,
          idx === 5 ? 0.5 : 0.2,
          idx === 5 ? 1 : idx === 4 ? 0.1 : 0
        );
      }
    });

    // Main product box
    const cube = new THREE.Mesh(mainBoxGeometry.clone(), materials);
    cube.rotation.y = 0;

    // Extra thin layer on the back
    const layerMaterial = new THREE.MeshStandardMaterial({
      // Updated color from #eaeaea to #f2f2f2 for a slightly lighter gray
      color: 0xf2f2f2,
      metalness: 0.6,
      roughness: 0.3,
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: 1
    });
    const layerMesh = new THREE.Mesh(layerGeometry, layerMaterial);
    // The main box is 0.04 thick; back face at z = -0.02
    // The layer is 0.01 thick (half = 0.005), so center at ~ -0.026
    layerMesh.position.z = -0.026;

    // Small black square behind the layer
    const smallBlackSquareMaterial = new THREE.MeshStandardMaterial({
      color: 0x1e1e1e,
      metalness: 0.1,
      roughness: 0.3,
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: 1
    });
    const blackSquare = new THREE.Mesh(backSquareGeometry, smallBlackSquareMaterial);
    // The black square is 0.05 thick, so we push it further behind
    blackSquare.position.z = layerMesh.position.z - 0.005 - 0.025 - 0.001; // ≈ -0.057

    // Group them
    const modelGroup = new THREE.Group();
    modelGroup.add(cube);
    modelGroup.add(layerMesh);
    modelGroup.add(blackSquare);
    // Optional horizontal offset
    modelGroup.position.x = -0.5;
    scene.add(modelGroup);

    // OrbitControls target
    controls.target.copy(modelGroup.position);

    // Animation loop
    const clock = new THREE.Clock();
    function animate() {
      requestAnimationFrame(animate);
      if (autoRotateMode === "default") {
        modelGroup.rotation.y = oscillationAmplitude * Math.sin(clock.getElapsedTime() * oscillationSpeed);
      }
      controls.update();
      renderer.render(scene, camera);
    }
    animate();

    // Resize handling
    let resizeTimeout;
    function handleResize() {
      const newWidth = container.clientWidth;
      const newHeight = container.clientHeight;
      renderer.setSize(newWidth, newHeight);
      renderer.setPixelRatio(window.devicePixelRatio || 1);
      camera.aspect = newWidth / newHeight;
      camera.updateProjectionMatrix();
    }
    window.addEventListener("resize", () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(handleResize, 100);
    });

    // Rotate hint logic
    window.addEventListener("DOMContentLoaded", function() {
      const hint = document.querySelector('.rotate-hint');
      if (hint) {
        hint.classList.add('spinY');
        const container = document.querySelector('.threejs-container');
        if (container) {
          function hideHint() {
            hint.style.opacity = '0';
            hint.classList.remove('spinY');
            container.removeEventListener('mousedown', hideHint);
            container.removeEventListener('touchstart', hideHint);
            container.removeEventListener('wheel', hideHint);
          }
          container.addEventListener('mousedown', hideHint);
          container.addEventListener('touchstart', hideHint);
          container.addEventListener('wheel', hideHint);
        }
      }
    });
  });
});
